import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import {
  createMentionedInSection,
  createPartnerMenuSection,
  createListingSection,
  createFooterMenuSection,
  createFooterEndSection,
} from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "31px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1450px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 50px",
      },
      responsiveStyles: {
        tablet: { padding: "0 16px" },
        mobile: { padding: "0 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            tablet: {
              display: "inline-block",
            },
          },
          children: [
            {
              id: generateId(),
              type: "link" as const,
              href: "/contact-us",
              content: "Contact Us",
              styles: {
                fontSize: "18px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "forground",
                textTransform: "capitalize",
                textDecoration: "none",
                position: "relative",
                transition: "all 0.3s ease",
              },
              typography: "Body",
              responsiveStyles: {
                tablet: {
                  fontSize: "14px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Thank You Hero Section ======================
const createThankYouHeroSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundImage: "url(/images/movyo/upsell/featureBox.svg)",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "48px",
    paddingTop: "100px",
    overflow: "hidden",
    width: "100%",
  },
  responsiveStyles: {
    mobile: {
      overflow: "hidden",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1450px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "48px",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          flexDirection: "column",
          gap: "24px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            width: "50%",
            position: "relative",
          },
          responsiveStyles: {
            mobile: {
              width: "300px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/invlop.svg",
              alt: "Thank You Header",
              styles: {
                maxWidth: "1300px",
                height: "1068.029px",
                flexShrink: "0",
                position: "absolute",
                top: "50%",
                left: "52%",
                transform: "translate(-50%, -50%) rotate(0.454deg)",
              },
              responsiveStyles: {
                mobile: {
                  maxWidth: "500px",
                },
                tablet: {
                  maxWidth: "741px",
                },
                desktop: {
                  maxWidth: "900px",
                },
              },
            },
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                width: "100%",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                padding: "50px 50px 0",
              },
              responsiveStyles: {
                mobile: {
                  padding: "11px 40px 0",
                },
                tablet: {
                  padding: "10px 12px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as const,
                  src: "/images/movyo/logo.webp",
                  alt: "Thank You Card 1",
                  styles: {
                    width: "232px",
                    height: "auto",
                  },
                  responsiveStyles: {
                    mobile: {
                      width: "100px",
                    },
                    tablet: {
                      width: "100px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Thank you for your order\nWe have received it",
                  styles: {
                    color: "link",
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "48px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "100%",
                    padding: "18px 0 30px",
                    margin: "0",
                    whiteSpace: "pre-line",
                  },
                  responsiveStyles: {
                    mobile: {
                      fontSize: "16px",
                      padding: "8px 0",
                    },
                    tablet: {
                      fontSize: "22px",
                      padding: "10px 0 16px",
                    },
                    desktop: {
                      fontSize: "28px",
                      padding: "10px 0",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We're excited to have you on board. Your order is on its way to you, and we'll send you a confirmation email shortly.",
                  styles: {
                    color: "link",
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "24px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "30.198px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    mobile: {
                      fontSize: "12px",
                      lineHeight: "16px",
                    },
                    tablet: {
                      fontSize: "14px",
                      lineHeight: "18px",
                    },
                    desktop: {
                      fontSize: "20px",
                      lineHeight: "26px",
                    },
                  },
                },
              ],
            },
          ],
        },
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            width: "50%",
            display: "flex",
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/doctor.svg",
              alt: "Thank You Header",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "cover",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Thank You Cards Section ======================
const createThankYouCardsSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "40px 0 190px",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "25px 0 120px",
    },
    mobile: {
      padding: "20px 0 80px",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1400px",
        margin: "0 auto",
        width: "100%",
        padding: "0 20px",
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "54px",
          },
          responsiveStyles: {
            tablet: {
              paddingBottom: "20px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as const,
              content:
                "<strong>Important:</strong> Please note that your payment will appear as Digistore24 on your bank or credit card statement.",
              styles: {
                color: "forground",
                fontFamily: '"SF Pro Display", sans-serif',
                fontSize: "28px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "100%",
                textAlign: "center",
                margin: "0",
              },
              responsiveStyles: {
                tablet: {
                  fontSize: "20px",
                  lineHeight: "110%",
                },
                mobile: {
                  fontSize: "18px",
                  lineHeight: "130%",
                },
              },
            },
          ],
        },
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "stretch",
            justifyContent: "center",
            gap: "40px 50px",
          },
          responsiveStyles: {
            tablet: {
              gap: "30px",
            },
          },
          children: [
            // Shipping Time Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "30px",
                  gap: "24px",
                },
                mobile: {
                  padding: "15px",
                  flex: "1 1 100%",
                  gap: "16px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/box.webp",
                      alt: "Thank You Card 1",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "60px",
                          height: "60px",
                        },
                        mobile: {
                          width: "40px",
                          height: "40px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content: "Shipping Time",
                      styles: {
                        color: "forground",
                        fontSize: "50px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "30px",
                        },
                        mobile: {
                          fontSize: "26px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Most orders arrive within 5-7 business days (U.S) or 10-15 days internationally.",
                      styles: {
                        color: "link",
                        fontSize: "26.4px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "33.6px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "18px",
                          lineHeight: "25px",
                        },
                        mobile: {
                          fontSize: "16px",
                          lineHeight: "22px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Bonus Delivery Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "30px",
                  gap: "24px",
                },
                mobile: {
                  padding: "15px",
                  flex: "1 1 100%",
                  gap: "16px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/gift.webp",
                      alt: "Thank You Card 1",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "60px",
                          height: "60px",
                        },
                        mobile: {
                          width: "40px",
                          height: "40px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content: "Bonus Delivery",
                      styles: {
                        color: "forground",
                        fontSize: "50px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "30px",
                        },
                        mobile: {
                          fontSize: "26px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "If your order included bonus ebooks, you'll find them in your inbox shortly. Be sure to check your spam or promotions folder just in case.",
                      styles: {
                        color: "link",
                        fontSize: "26.4px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "33.6px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "18px",
                          lineHeight: "25px",
                        },
                        mobile: {
                          fontSize: "16px",
                          lineHeight: "22px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Need Help Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "30px",
                  gap: "24px",
                },
                mobile: {
                  padding: "15px",
                  flex: "1 1 100%",
                  gap: "16px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/question.webp",
                      alt: "Thank You Card 1",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "60px",
                          height: "60px",
                        },
                        mobile: {
                          width: "40px",
                          height: "40px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content: "Need Help?",
                      styles: {
                        color: "forground",
                        fontSize: "50px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "30px",
                        },
                        mobile: {
                          fontSize: "26px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: "16px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Have questions about your order or need support? Our friendly team is ready to help:",
                      styles: {
                        color: "link",
                        fontSize: "26.4px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "33.6px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "18px",
                          lineHeight: "25px",
                        },
                        mobile: {
                          fontSize: "16px",
                          lineHeight: "22px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        gap: "8px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content: "<strong>Email:</strong> support@movyo.net",
                          styles: {
                            color: "link",
                            fontSize: "26.4px",
                            fontStyle: "normal",
                            fontWeight: "400",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: {
                              fontSize: "18px",
                              lineHeight: "25px",
                            },
                            mobile: {
                              fontSize: "16px",
                              lineHeight: "22px",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content:
                            "<strong>Hours:</strong> Monday - Friday, 9AM-5PM EST",
                          styles: {
                            color: "link",
                            fontSize: "26.4px",
                            fontStyle: "normal",
                            fontWeight: "400",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: {
                              fontSize: "18px",
                              lineHeight: "25px",
                            },
                            mobile: {
                              fontSize: "16px",
                              lineHeight: "22px",
                            },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // What's Next Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                padding: "50px",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                flex: "1 1 calc(50% - 25px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "45px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "30px",
                  gap: "24px",
                },
                mobile: {
                  padding: "15px",
                  flex: "1 1 100%",
                  gap: "16px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/time.webp",
                      alt: "Thank You Card 1",
                      styles: {
                        width: "90px",
                        height: "90px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "60px",
                          height: "60px",
                        },
                        mobile: {
                          width: "40px",
                          height: "40px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content: "What's Next?",
                      styles: {
                        color: "forground",
                        fontSize: "50px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "36px",
                        },
                        mobile: {
                          fontSize: "30px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "While you wait, check your bonus email for our welcome guide and tips to get the most from Movyo! Want health tips, product updates, and exclusive offers?",
                      styles: {
                        color: "link",
                        fontSize: "26.4px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "33.6px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "24px",
                        },
                        mobile: {
                          fontSize: "20px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Newsletter Section ======================
const createNewsletterSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundImage: "linear-gradient(0deg, #a838b9 0%, #7d65cc 100%)",
    position: "relative",
    width: "100%",
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        position: "relative",
        padding: "150px 0 80px",
        width: "100%",
        maxWidth: "1400px",
        margin: "0 auto",
      },
      responsiveStyles: {
        tablet: { padding: "100px 20px 60px" },
        mobile: { padding: "100px 20px 40px" },
      },
      children: [
        // Background image
        {
          id: generateId(),
          type: "image" as const,
          src: "/images/movyo/congratualtions.webp",
          alt: "Thank You Congratulations",
          styles: {
            position: "absolute",
            top: "-20px",
            left: "50%",
            transform: "translate3d(-50%, -40%, 0)",
            width: "100%",
            objectFit: "contain",
            maxWidth: "217.729px",
            zIndex: "1",
          },
          responsiveStyles: {
            mobile: { maxWidth: "130px" },
            tablet: { maxWidth: "120px" },
          },
        },
        // Rhombus
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            width: "50px",
            height: "50px",
            backgroundColor: "#A838B9",
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translate3d(-50%, 50%, 0) rotate(45deg)",
            zIndex: "2",
          },
          responsiveStyles: {
            mobile: { width: "30px", height: "30px" },
          },
        },
        // Newsletter box
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "95px",
            position: "relative",
            zIndex: "3",
          },
          responsiveStyles: {
            tablet: { gap: "45px" },
            mobile: { gap: "18px" },
          },
          children: [
            // Header
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "12px",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "You're Officially One of Us",
                  styles: {
                    color: "background",
                    fontSize: "50px",
                    fontWeight: "700",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "30px", textAlign: "center" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "You didn't just place an order. You made a commitment to better health - and we don't take that lightly. From now on, you'll be treated like one of our VIP insiders:",
                  styles: {
                    color: "background",
                    textAlign: "center",
                    fontSize: "28px",
                    fontWeight: "300",
                    lineHeight: "100%",
                    maxWidth: "1342px",
                    width: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px" },
                    mobile: { fontSize: "18px", textAlign: "center" },
                  },
                },
              ],
            },
            // Content
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "45px",
              },
              responsiveStyles: {
                tablet: { gap: "24px" },
              },
              children: [
                // Cards
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "50px",
                    alignItems: "flex-start",
                  },
                  responsiveStyles: {
                    tablet: { gap: "16px" },
                    mobile: {
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "24px 16px",
                    },
                  },
                  children: [
                    // Card 1
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as const,
                          src: "/images/movyo/newsletter1.svg", // You need to add this SVG to your images
                          alt: "Early access",
                          styles: {
                            width: "104px",
                            height: "107px",
                          },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content: "Early access to new formulas",
                          styles: {
                            color: "background",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "300",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "20px", lineHeight: "26px" },
                            mobile: { fontSize: "16px", lineHeight: "22px" },
                          },
                        },
                      ],
                    },
                    // Card 2
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as const,
                          src: "/images/movyo/newsletter2.svg", // You need to add this SVG to your images
                          alt: "Priority Support",
                          styles: {
                            width: "92px",
                            height: "102px",
                          },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content: "Priority Support",
                          styles: {
                            color: "background",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "300",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "20px", lineHeight: "26px" },
                            mobile: { fontSize: "16px", lineHeight: "22px" },
                          },
                        },
                      ],
                    },
                    // Card 3
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as const,
                          src: "/images/movyo/newsletter3.svg", // You need to add this SVG to your images
                          alt: "Special VIP only bundles",
                          styles: {
                            width: "104px",
                            height: "104px",
                          },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content: "Special VIP only bundles",
                          styles: {
                            color: "background",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "300",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "20px", lineHeight: "26px" },
                            mobile: { fontSize: "16px", lineHeight: "22px" },
                          },
                        },
                      ],
                    },
                    // Card 4
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as const,
                          src: "/images/movyo/newsletter4.svg", // You need to add this SVG to your images
                          alt: "Monthly health drop-ins",
                          styles: {
                            width: "99px",
                            height: "103px",
                          },
                          responsiveStyles: {
                            tablet: {
                              maxWidth: "60px",
                              maxHeight: "60px",
                              width: "100%",
                            },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as const,
                          content:
                            "Monthly health drop-ins from Dr. Alexandra Sterling himself",
                          styles: {
                            color: "background",
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "300",
                            lineHeight: "33.6px",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "20px", lineHeight: "26px" },
                            mobile: { fontSize: "16px", lineHeight: "22px" },
                          },
                        },
                      ],
                    },
                  ],
                },
                // Welcome message
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Welcome to the Movyo family — we're with you every step of the way.",
                  styles: {
                    color: "background",
                    fontSize: "30px",
                    fontWeight: "500",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "24px", textAlign: "center" },
                    mobile: { fontSize: "18px", textAlign: "center" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Get Ready Section ======================
const createGetReadySection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "#E6E7E8",
    padding: "100px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: { padding: "60px 20px" },
    mobile: { padding: "40px 20px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1400px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: "60px",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { gap: "16px" },
      },
      children: [
        // Box 1
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexDirection: "column",
            gap: "70px",
            maxWidth: "1280px",
            margin: "0 auto",
            width: "100%",
          },
          responsiveStyles: {
            tablet: { gap: "45px" },
            mobile: { gap: "24px" },
          },
          children: [
            // Header
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "20px",
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as const,
                  src: "/images/movyo/call.svg",
                  alt: "Phone Icon",
                  styles: {
                    width: "65px",
                    height: "65px",
                    objectFit: "contain",
                  },
                  responsiveStyles: {
                    tablet: { width: "40px", height: "40px" },
                    mobile: { width: "30px", height: "30px" },
                  },
                },
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Get Ready - Your VIP Wellness Call is Coming",
                  styles: {
                    color: "forground",
                    fontSize: "57px",
                    fontWeight: "700",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "30px" },
                    mobile: { fontSize: "24px" },
                  },
                },
              ],
            },
            // Content
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "20px",
                width: "100%",
              },
              responsiveStyles: {
                mobile: { gap: "16px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "You've just joined the MOVYO inner circle - and that means you qualify for a 1-on-1 VIP Support Call with our wellness team.",
                  styles: {
                    color: "link",
                    fontSize: "28px",
                    fontWeight: "400",
                    lineHeight: "33.6px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "20px", lineHeight: "26px" },
                    mobile: { fontSize: "16px", lineHeight: "22px" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We'll check in to welcome you personally, answer any questions you have about MOVYO, and help you get the absolute best results possible.",
                  styles: {
                    color: "link",
                    fontSize: "28px",
                    fontWeight: "400",
                    lineHeight: "33.6px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "20px", lineHeight: "26px" },
                    mobile: { fontSize: "16px", lineHeight: "22px" },
                  },
                },
                // List
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content: "What to expect:",
                      styles: {
                        color: "link",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "28px",
                        fontWeight: "700",
                        lineHeight: "100%",
                        paddingBottom: "16px",
                        margin: "0",
                      },
                      typography: "Heading-5",
                    },
                    {
                      id: generateId(),
                      type: "container" as const,
                      styles: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "0px",
                      },
                      children: [
                        // List Item 1
                        {
                          id: generateId(),
                          type: "container" as const,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content: "✓",
                              styles: {
                                color: "#CF178F",
                                fontSize: "32px",
                                fontWeight: "700",
                                lineHeight: "150%",
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content:
                                "A personal email or SMS in the next 24-48 hours to book your slot",
                              styles: {
                                color: "link",
                                fontSize: "28px",
                                fontWeight: "400",
                                lineHeight: "33.6px",
                                margin: "0",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "20px",
                                  lineHeight: "26px",
                                },
                                mobile: {
                                  fontSize: "16px",
                                  lineHeight: "22px",
                                },
                              },
                            },
                          ],
                        },
                        // List Item 2
                        {
                          id: generateId(),
                          type: "container" as const,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content: "✓",
                              styles: {
                                color: "#CF178F",
                                fontSize: "32px",
                                fontWeight: "700",
                                lineHeight: "150%",
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content:
                                "Tips on getting started with your protocol",
                              styles: {
                                color: "link",
                                fontSize: "28px",
                                fontWeight: "400",
                                lineHeight: "33.6px",
                                margin: "0",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "20px",
                                  lineHeight: "26px",
                                },
                                mobile: {
                                  fontSize: "16px",
                                  lineHeight: "22px",
                                },
                              },
                            },
                          ],
                        },
                        // List Item 3
                        {
                          id: generateId(),
                          type: "container" as const,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content: "✓",
                              styles: {
                                color: "#CF178F",
                                fontSize: "32px",
                                fontWeight: "700",
                                lineHeight: "150%",
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as const,
                              content: "Answers to any burning questions",
                              styles: {
                                color: "link",
                                fontSize: "28px",
                                fontWeight: "400",
                                lineHeight: "33.6px",
                                margin: "0",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "20px",
                                  lineHeight: "26px",
                                },
                                mobile: {
                                  fontSize: "16px",
                                  lineHeight: "22px",
                                },
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        // Headline
        {
          id: generateId(),
          type: "heading" as const,
          content:
            "This is your moment - let's make your transformation unstoppable.",
          styles: {
            color: "#7D66CC",
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
            fontSize: "36px",
            fontStyle: "italic",
            fontWeight: "900",
            lineHeight: "100%",
            maxWidth: "1342px",
            width: "100%",
            margin: "0 auto",
          },
          responsiveStyles: {
            tablet: { fontSize: "20px", lineHeight: "26px", textAlign: "left" },
            mobile: { fontSize: "16px", lineHeight: "22px", textAlign: "left" },
          },
        },
      ],
    },
  ],
});

export const template2Thankyou = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    hero: createThankYouHeroSection(globalConfig) as BuilderElement,
    thankYouCards: createThankYouCardsSection(globalConfig) as BuilderElement,
    newsletter: createNewsletterSection(globalConfig) as BuilderElement,
    getReady: createGetReadySection(globalConfig) as BuilderElement,
    mentioned: createMentionedInSection(globalConfig) as BuilderElement,
    partnerMenu: createPartnerMenuSection(globalConfig) as BuilderElement,
    listing: createListingSection(globalConfig) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2ThankyouGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
